import { endOfDay } from 'date-fns';
import { TFunction } from 'i18next';

import { getMessagesControl } from 'providers/Toast';

import {
  LoggedUser,
  FullProject,
  isOwner,
  projectStatusByCode,
  taskStatusesConfig,
  isInvoiceRepriceActive,
} from 'entities';

export { isOwner, isInvoiceRepriceActive };

export const MESSAGES = getMessagesControl({
  startFormFilling: (t) => ({
    title: t('please-choose-all-expected-dates'),
  }),

  activeRecruitment: (t) => ({
    title: t('project-has-active-recruitment'),
    type: 'error',
  }),

  activeIssue: (t) => ({
    title: t('project-has-active-issue'),
    type: 'error',
  }),

  startFail: (t) => ({
    title: t('could-not-start-project'),
    type: 'error',
  }),

  startSuccess: (t, endDate) => ({
    title: t('project-started-with-success'),
    type: 'success',
    description: t('your-expected-deadline-is-endDate', { endDate }) as string,
  }),

  uploadFail: (t) => ({
    title: t('fail-to-upload-file'),
    description: t('try-again-in-a-few-moments') as string,
    type: 'error',
  }),

  dateChangeError: (t) => ({
    title: t('error-while-executing-date-change-request'),
    description: t('try-again-in-a-few-moments') as string,
    type: 'error',
  }),

  dateChangeSuccess: (t) => ({
    title: t('task-expected-date-changed'),
    type: 'success',
  }),

  dateChangeRejectSuccess: (t) => ({
    title: t('task-expected-date-was-not-changed'),
    type: 'success',
  }),

  excludedIdsMismatch: (t) => ({
    title: t('some-ids-were-not-found-on-sample'),
    description: t(
      'ids-not-found-were-excluded-please-double-check-consolidated-ids-and-update-data-if-necessary',
    ) as string,
  }),

  sampleIdsMismatch: (t) => ({
    title: t('some-ids-were-not-found-on-sample'),
    description: t(
      'ids-not-found-were-excluded-please-double-check-consolidated-ids-and-update-data-if-necessary',
    ) as string,
  }),

  projectDoneWithoutTasksDone: (t) => ({
    title: t('project-still-has-unfinished-tasks'),
    description: t('make-sure-all-of-them-are-complete-first') as string,
    type: 'error',
  }),

  statusChangeFail: (t) => ({
    title: t('could-not-change-project-status'),
    description: t('try-again-in-a-few-moments') as string,
    type: 'error',
  }),

  statusChangeAborted: (t) => ({
    title: t('status-change-aborted'),
  }),

  projectDateChangeAborted: (t) => ({
    title: t('date-change-aborted'),
  }),

  changeDates: (t) => ({
    title: t('project-did-not-accomplish-expected-date'),
    description: t(
      'please-request-a-date-change-and-provide-context-before-changing-project-to-done',
    ) as string,
    type: 'error',
  }),

  cantFinishOnThisStatus: (t) => ({
    title: t('cant-finish-project-on-this-status'),
    type: 'error',
  }),

  finishedBefore: (t) => ({
    title: t('project-has-been-set-to-done-before'),
    description: t('it-cant-be-canceled') as string,
    type: 'error',
  }),

  notSentToInvoice: (t) => ({
    title: t('project-has-not-been-sent-to-invoice'),
    description: t('send-it-before-canceling') as string,
    type: 'error',
  }),

  dateChangeRequestSent: (t) => ({
    title: t('date-change-request-has-been-sent'),
    description: t('your-area-leaders-will-review-it-now') as string,
    type: 'success',
  }),

  projectDateChanged: (t) => ({
    title: t('project-date-changed'),
    description: t('task-owners-will-be-notified') as string,
    type: 'success',
  }),

  cancelTaskAborted: (t) => ({
    title: t('task-cancellation-aborted'),
  }),

  cancelProjectAborted: (t) => ({
    title: t('project-cancellation-aborted'),
  }),

  reactivateProjectAborted: (t) => ({
    title: t('project-reactivation-aborted'),
  }),

  projectTypeCancelBlock: (t) => ({
    title: t('this-project-type-cant-be-cancelled-right-now'),
    type: 'error',
  }),

  projectOwnerChangeAborted: (t) => ({
    title: t('project-ownership-change-aborted'),
  }),

  projectCancelWithPendingTasks: (t) => ({
    title: t('project-still-has-tasks-pending-approval'),
    description: t('you-cant-cancel-on-this-state') as string,
  }),

  projectCancelWithDoneTasks: (t) => ({
    title: t('project-has-all-tasks-marked-as-done-already'),
    description: t('you-cant-cancel-on-this-state') as string,
  }),

  addCountryFail: (t) => ({
    title: t('error-while-adding-country'),
    type: 'error',
  }),

  addCountryCommentOnReject: (t) => ({
    title: t('comment-required'),
    description: t('its-required-if-the-approval-was-reject') as string,
  }),
});

export const completionValidations = [
  {
    getError: (t: TFunction) => MESSAGES.projectDoneWithoutTasksDone(t),
    checkError: (project?: FullProject) => {
      if (!project) return true;

      const codesThatCanFinish = [taskStatusesConfig.done.code, taskStatusesConfig.canceled.code];

      return project?.tasks.some((task) => !codesThatCanFinish.includes(task.status));
    },
  },
  {
    getError: (t: TFunction) => MESSAGES.changeDates(t),
    checkError: (project?: FullProject) => {
      if (!project) return true;

      const expectedDateHasPassed =
        Number(new Date()) > Number(endOfDay(new Date(project.expectedDate as string)));

      return expectedDateHasPassed;
    },
  },
  {
    getError: (t: TFunction) => MESSAGES.cantFinishOnThisStatus(t),
    checkError: (project?: FullProject) => {
      if (!project) return true;

      const { canFinish } = projectStatusByCode[project.status];

      return !canFinish;
    },
  },
  {
    getError: (t: TFunction) => MESSAGES.activeRecruitment(t),
    checkError: (project?: FullProject) => !!project?.activeRecruitments,
  },
  {
    getError: (t: TFunction) => MESSAGES.activeIssue(t),
    checkError: (project?: FullProject) => !!project?.activeIssues,
  },
];

export function determineProjectCanBeCompleted(
  project: FullProject | undefined,
  user: LoggedUser,
): boolean {
  const canMarkAsDone = [
    isOwner(project, user),
    completionValidations.every(({ checkError }) => !checkError(project)),
  ].every(Boolean);

  return canMarkAsDone;
}
